// src/utils/smsCampaignModel.js

/**
 * SMS Campaign Status configurations
 */
export const SMS_CAMPAIGN_STATUS = {
  DRAFT: 'draft',
  SENDING: 'sending',
  RUNNING: 'running', 
  COMPLETED: 'completed',
  FAILED: 'failed'
}

/**
 * SMS Campaign Status display configurations
 */
export const SMS_CAMPAIGN_STATUS_CONFIG = {
  [SMS_CAMPAIGN_STATUS.DRAFT]: {
    label: 'Draft',
    color: 'bg-gray-100 text-gray-800',
    icon: '📝',
    description: 'Campaign is ready to be sent'
  },
  [SMS_CAMPAIGN_STATUS.SENDING]: {
    label: 'Sending',
    color: 'bg-orange-100 text-orange-800',
    icon: '📤',
    description: 'Campaign is being prepared for sending'
  },
  [SMS_CAMPAIGN_STATUS.RUNNING]: {
    label: 'Running',
    color: 'bg-blue-100 text-blue-800',
    icon: '🏃',
    description: 'Campaign is currently being sent'
  },
  [SMS_CAMPAIGN_STATUS.COMPLETED]: {
    label: 'Completed',
    color: 'bg-green-100 text-green-800',
    icon: '✅',
    description: 'Campaign has been sent successfully'
  },
  [SMS_CAMPAIGN_STATUS.FAILED]: {
    label: 'Failed',
    color: 'bg-red-100 text-red-800',
    icon: '❌',
    description: 'Campaign failed to send'
  }
}

/**
 * SMS Log Status configurations
 */
export const SMS_LOG_STATUS = {
  SUCCESS: 'success',
  FAILED: 'failed',
  PENDING: 'pending'
}

/**
 * SMS Log Status display configurations
 */
export const SMS_LOG_STATUS_CONFIG = {
  [SMS_LOG_STATUS.SUCCESS]: {
    label: 'Success',
    color: 'bg-green-100 text-green-800',
    icon: '✅'
  },
  [SMS_LOG_STATUS.FAILED]: {
    label: 'Failed',
    color: 'bg-red-100 text-red-800', 
    icon: '❌'
  },
  [SMS_LOG_STATUS.PENDING]: {
    label: 'Pending',
    color: 'bg-yellow-100 text-yellow-800',
    icon: '⏳'
  }
}

/**
 * SMS Campaign Model class for data validation and formatting
 */
export class SMSCampaignModel {
  /**
   * Validate SMS campaign data for creation
   * @param {Object} data - Campaign data to validate
   * @returns {Object} - Validation result with isValid flag and errors array
   */
  static validate(data) {
    console.log('🔍 Validating SMS campaign data:', data)
    
    const errors = []
    
    // Required fields validation
    if (!data.title || data.title.trim().length === 0) {
      errors.push('Campaign title is required')
    }
    
    if (!data.customerGroup) {
      errors.push('Customer group is required')
    }
    
    // Title length validation
    if (data.title && data.title.trim().length > 200) {
      errors.push('Campaign title cannot exceed 200 characters')
    }
    
    // Customer Group ID validation
    if (data.customerGroup && typeof data.customerGroup !== 'string') {
      errors.push('Customer group must be a valid ID string')
    }
    
    const isValid = errors.length === 0
    
    console.log(isValid ? '✅ SMS campaign validation passed' : '❌ SMS campaign validation failed:', errors)
    
    return {
      isValid,
      errors
    }
  }

  /**
   * Validate SMS campaign data for update
   * @param {Object} data - Campaign data to validate
   * @returns {Object} - Validation result with isValid flag and errors array
   */
  static validateUpdate(data) {
    console.log('🔍 Validating SMS campaign data for update:', data)
    
    const errors = []
    
    // Title validation (if provided)
    if (data.title !== undefined) {
      if (!data.title || data.title.trim().length === 0) {
        errors.push('Campaign title cannot be empty')
      } else if (data.title.trim().length > 200) {
        errors.push('Campaign title cannot exceed 200 characters')
      }
    }
    
    // Customer Group validation (if provided)
    if (data.customerGroup !== undefined) {
      if (!data.customerGroup) {
        errors.push('Customer group cannot be empty')
      } else if (typeof data.customerGroup !== 'string') {
        errors.push('Customer group must be a valid ID string')
      }
    }
    
    // Status validation (if provided)
    if (data.status !== undefined) {
      const validStatuses = Object.values(SMS_CAMPAIGN_STATUS)
      if (!validStatuses.includes(data.status)) {
        errors.push(`Invalid status. Must be one of: ${validStatuses.join(', ')}`)
      }
    }
    
    const isValid = errors.length === 0
    
    console.log(isValid ? '✅ SMS campaign update validation passed' : '❌ SMS campaign update validation failed:', errors)
    
    return {
      isValid,
      errors
    }
  }

  /**
   * Format SMS campaign data for display
   * @param {Object} campaign - Raw campaign data from API
   * @returns {Object} - Formatted campaign data
   */
  static formatForDisplay(campaign) {
    if (!campaign) return null
    
    try {
      console.log('🎨 Formatting SMS campaign for display:', campaign)
      
      const formatted = {
        id: campaign._id,
        title: campaign.title,
        status: campaign.status,
        statusConfig: this.getStatusConfig(campaign.status),
        customerGroup: {
          id: campaign.customerGroup?._id || campaign.customerGroup,
          name: campaign.customerGroup?.name || 'Unknown Group',
          description: campaign.customerGroup?.description || '',
          customerCount: campaign.customerGroup?.customers?.length || 0
        },
        createdBy: {
          id: campaign.createdBy?._id || campaign.createdBy,
          username: campaign.createdBy?.username || 'Unknown User',
          role: campaign.createdBy?.role || 'unknown'
        },
        updatedBy: campaign.updatedBy ? {
          id: campaign.updatedBy._id,
          username: campaign.updatedBy.username || 'Unknown User',
          role: campaign.updatedBy.role || 'unknown'
        } : null,
        createdAt: campaign.createdAt,
        updatedAt: campaign.updatedAt,
        createdAtFormatted: this.formatDate(campaign.createdAt),
        updatedAtFormatted: this.formatDate(campaign.updatedAt)
      }
      
      console.log('✅ SMS campaign formatted successfully:', formatted)
      return formatted
      
    } catch (error) {
      console.error('❌ Error formatting SMS campaign:', error)
      return campaign // Return original if formatting fails
    }
  }

  /**
   * Get status configuration for display
   * @param {string} status - Campaign status
   * @returns {Object} - Status configuration
   */
  static getStatusConfig(status) {
    return SMS_CAMPAIGN_STATUS_CONFIG[status] || {
      label: 'Unknown',
      color: 'bg-gray-100 text-gray-800',
      icon: '❓',
      description: 'Unknown status'
    }
  }

  /**
   * Get log status configuration for display
   * @param {string} status - Log status
   * @returns {Object} - Status configuration
   */
  static getLogStatusConfig(status) {
    return SMS_LOG_STATUS_CONFIG[status] || {
      label: 'Unknown',
      color: 'bg-gray-100 text-gray-800',
      icon: '❓'
    }
  }

  /**
   * Format SMS log data for display
   * @param {Object} log - Raw log data from API
   * @returns {Object} - Formatted log data
   */
  static formatLogForDisplay(log) {
    if (!log) return null
    
    try {
      console.log('🎨 Formatting SMS log for display:', log)
      
      const formatted = {
        id: log._id,
        campaign: {
          id: log.campaign?._id || log.campaign,
          title: log.campaign?.title || 'Unknown Campaign',
          status: log.campaign?.status || 'unknown'
        },
        customer: {
          id: log.customer?._id || log.customer,
          name: log.customer?.name || 'Unknown Customer',
          mobileNumber: log.customer?.mobileNumber || log.mobileNumber || 'Unknown'
        },
        mobileNumber: log.mobileNumber,
        status: log.status,
        statusConfig: this.getLogStatusConfig(log.status),
        errorMessage: log.errorMessage,
        attempts: log.attempts || 1,
        sentAt: log.sentAt,
        sentAtFormatted: log.sentAt ? this.formatDate(log.sentAt) : 'Not sent',
        createdAt: log.createdAt,
        updatedAt: log.updatedAt,
        createdAtFormatted: this.formatDate(log.createdAt),
        updatedAtFormatted: this.formatDate(log.updatedAt)
      }
      
      console.log('✅ SMS log formatted successfully:', formatted)
      return formatted
      
    } catch (error) {
      console.error('❌ Error formatting SMS log:', error)
      return log // Return original if formatting fails
    }
  }

  /**
   * Format date for display
   * @param {string} dateString - ISO date string
   * @returns {string} - Formatted date string
   */
  static formatDate(dateString) {
    if (!dateString) return 'N/A'
    
    try {
      const date = new Date(dateString)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
    } catch (error) {
      console.error('❌ Error formatting date:', error)
      return dateString
    }
  }

  /**
   * Get available actions based on campaign status
   * @param {string} status - Campaign status
   * @returns {Array} - Array of available actions
   */
  static getAvailableActions(status) {
    const actions = {
      [SMS_CAMPAIGN_STATUS.DRAFT]: ['edit', 'delete', 'run', 'view'],
      [SMS_CAMPAIGN_STATUS.SENDING]: ['view'], // No actions while sending
      [SMS_CAMPAIGN_STATUS.RUNNING]: ['view'], // No edit, delete, or run while running
      [SMS_CAMPAIGN_STATUS.FAILED]: ['run', 'retry', 'delete', 'view'], // Allow re-run for failed campaigns
      [SMS_CAMPAIGN_STATUS.COMPLETED]: ['retry', 'delete', 'view'] // No edit once complete
    }
    
    return actions[status] || ['view']
  }

  /**
   * Check if campaign can be edited
   * @param {string} status - Campaign status
   * @returns {boolean} - Whether campaign can be edited
   */
  static canEdit(status) {
    return status === SMS_CAMPAIGN_STATUS.DRAFT
  }

  /**
   * Check if campaign can be deleted
   * @param {string} status - Campaign status
   * @returns {boolean} - Whether campaign can be deleted
   */
  static canDelete(status) {
    return [SMS_CAMPAIGN_STATUS.DRAFT, SMS_CAMPAIGN_STATUS.FAILED, SMS_CAMPAIGN_STATUS.COMPLETED].includes(status)
  }

  /**
   * Check if campaign can be run
   * @param {string} status - Campaign status
   * @returns {boolean} - Whether campaign can be run
   */
  static canRun(status) {
    return status === SMS_CAMPAIGN_STATUS.DRAFT
  }

  /**
   * Check if campaign can be retried
   * @param {string} status - Campaign status
   * @returns {boolean} - Whether campaign can be retried
   */
  static canRetry(status) {
    return [SMS_CAMPAIGN_STATUS.FAILED, SMS_CAMPAIGN_STATUS.COMPLETED].includes(status)
  }

  /**
   * Get campaign priority for sorting (lower number = higher priority)
   * @param {string} status - Campaign status
   * @returns {number} - Priority number for sorting
   */
  static getStatusPriority(status) {
    const priorities = {
      [SMS_CAMPAIGN_STATUS.RUNNING]: 1,
      [SMS_CAMPAIGN_STATUS.FAILED]: 2,
      [SMS_CAMPAIGN_STATUS.DRAFT]: 3,
      [SMS_CAMPAIGN_STATUS.COMPLETED]: 4
    }
    
    return priorities[status] || 999
  }
}

export default SMSCampaignModel